import React, { useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import { getRangeCutoff } from "../../utils/chartUtils";
import ChartFrame from "../shared/ChartFrame";

const TIME_RANGES = [
  { label: "Inception", value: "all" },
  { label: "3Y", value: "3y" },
  { label: "1Y", value: "1y" },
  { label: "6M", value: "6m" },
  { label: "3M", value: "3m" },
  { label: "1M", value: "1m" },
];

// Function to calculate return for a specific range
function calculateReturn(values) {
  if (!values || values.length < 2) return null;
  const startValue = values[0];
  const endValue = values[values.length - 1];
  if (startValue === 0) return null;
  return ((endValue - startValue) / startValue) * 100;
}

const PerformanceChart = ({
  data,
  /** If you pass `timeframe`, the chart becomes controlled. Otherwise it manages its own. */
  timeframe,
  /** Show the timeframe pills inside the chart card (default: true) */
  showTimeframeSelector = true,
  isDarkMode,
  aspect = 16 / 9,
  minHeight = 220,
  maxHeight = 420,
  fixedHeight,
}) => {
  const allDates = data?.labels || [];
  const allValues = (data?.datasets && data.datasets[0]?.data) || [];

  // Internal timeframe state (used when caller doesn't control it)
  const [internalTimeframe, setInternalTimeframe] = useState("all");
  const [hoveredTimeframe, setHoveredTimeframe] = useState(null);
  const effectiveTimeframe = timeframe ?? internalTimeframe;

  // Calculate start index based on timeframe
  const startIdx = getRangeCutoff(effectiveTimeframe, allDates);
  const filteredDates = allDates.slice(startIdx);
  const filteredValues = allValues.slice(startIdx);

  // Precompute returns for each timeframe (used for the small badge near pills)
  const timeframeReturns = useMemo(() => {
    if (!allValues?.length) return {};
    const returns = {};
    TIME_RANGES.forEach(({ value }) => {
      const idx = getRangeCutoff(value, allDates);
      const values = allValues.slice(idx);
      const pct = calculateReturn(values);
      if (pct !== null) returns[value] = pct;
    });
    return returns;
  }, [allDates, allValues]);

  const chartData = useMemo(
    () => ({
      labels: filteredDates,
      datasets: [
        {
          data: filteredValues,
          borderColor: isDarkMode ? "#60A5FA" : "#1E3A8A",
          backgroundColor: isDarkMode ? "rgba(96, 165, 250, 0.1)" : "rgba(30, 58, 138, 0.1)",
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 0,
          borderWidth: 2,
        },
      ],
    }),
    [filteredDates, filteredValues, isDarkMode],
  );

  const chartOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      elements: {
        line: { borderWidth: 2 },
        point: { radius: 0 },
      },
      layout: {
        padding: {
          top: 10,
          bottom: 20, // Add more bottom padding to prevent cutoff
          left: 10,
          right: 10
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: isDarkMode ? 'rgba(17, 24, 39, 0.8)' : 'rgba(255, 255, 255, 0.95)',
          titleColor: isDarkMode ? '#fff' : '#111827',
          bodyColor: isDarkMode ? '#fff' : '#111827',
          borderColor: isDarkMode ? 'rgba(75, 85, 99, 0.3)' : 'rgba(209, 213, 219, 0.5)',
          borderWidth: 1,
          padding: 10,
          callbacks: {
            label: (ctx) => {
              const idx = ctx.dataIndex;
              const startVal = filteredValues[0];
              const currVal = filteredValues[idx];
              const pct = startVal ? ((currVal - startVal) / startVal) * 100 : null;
              return [
                `Value: $${currVal?.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
                pct !== null
                  ? `Return: ${pct >= 0 ? "+" : ""}${pct?.toFixed(2)}%`
                  : "",
              ];
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            color: isDarkMode ? 'rgba(75, 85, 99, 0.1)' : 'rgba(209, 213, 219, 0.25)',
            borderColor: isDarkMode ? 'rgba(75, 85, 99, 0.2)' : 'rgba(209, 213, 219, 0.5)',
          },
          ticks: {
            color: isDarkMode ? '#fff' : '#4B5563',
            maxRotation: 45,
            minRotation: 45,
            source: 'data',
            autoSkip: true,
            maxTicksLimit: 8,
            font: {
              size: 10
            },
            callback: (val, idx, ticks) => {
              const date = new Date(filteredDates[idx]);
              return date.toLocaleDateString("en-US", { 
                month: "short",
                year: "2-digit"
              });
            },
          },
        },
        y: {
          grid: {
            color: isDarkMode ? 'rgba(75, 85, 99, 0.1)' : 'rgba(209, 213, 219, 0.25)',
            borderColor: isDarkMode ? 'rgba(75, 85, 99, 0.2)' : 'rgba(209, 213, 219, 0.5)',
          },
          ticks: {
            color: isDarkMode ? '#fff' : '#4B5563',
            font: {
              size: 10
            },
            callback: (v) =>
              `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
          },
        },
      },
    }),
    [filteredDates, filteredValues, isDarkMode],
  );

  return (
    <ChartFrame aspect={aspect} minHeight={minHeight} maxHeight={maxHeight} fixedHeight={fixedHeight}>
      {showTimeframeSelector && (
        <div className="flex justify-end mb-3">
          <div className="flex flex-wrap gap-1 min-w-[240px] max-w-[440px]">
            {TIME_RANGES.map((tf) => (
              <div key={tf.value} className={`relative flex-1 ${tf.value === "all" ? "min-w-[60px]" : "min-w-[32px]"} max-w-[80px]`}>
                <button
                  onClick={() => setInternalTimeframe(tf.value)}
                  onMouseEnter={() => setHoveredTimeframe(tf.value)}
                  onMouseLeave={() => setHoveredTimeframe(null)}
                  className={`
                    w-full px-2 py-[2px] text-[10px] rounded-full transition-all
                    ${effectiveTimeframe === tf.value
                      ? "bg-blue-600 text-white dark:bg-gray-800 dark:text-emerald-400 dark:border-2 dark:border-emerald-500/50 dark:shadow-[0_0_10px_-3px_rgba(16,185,129,0.5)]"
                      : "bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700 dark:border dark:border-gray-700"}
                  `}
                  disabled={timeframe !== undefined} /* controlled externally */
                  title={timeframe !== undefined ? "Timeframe controlled by parent" : undefined}
                >
                  {tf.label}
                </button>
                {(hoveredTimeframe === tf.value || effectiveTimeframe === tf.value) &&
                  timeframeReturns[tf.value] != null && (
                    <div
                      className={`
                        absolute bottom-0 right-0 translate-y-full px-2 py-[2px]
                        rounded-full text-[10px] font-medium shadow-lg backdrop-blur-sm
                        ${timeframeReturns[tf.value] >= 0
                          ? "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400"
                          : "bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-400"}
                        border border-gray-200 dark:border-gray-700
                        whitespace-nowrap z-10
                      `}
                    >
                      {timeframeReturns[tf.value] >= 0 ? "+" : ""}
                      {timeframeReturns[tf.value].toFixed(2)}%
                    </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="w-full h-full flex-1 min-h-0">
        <Line data={chartData} options={chartOptions} />
      </div>
    </ChartFrame>
  );
};

export default PerformanceChart;
